import { getSiteCountries } from '@_api/siteCountries';
import { getClassFunction } from '@helpers/functions';
import { SiteCountryModelApiResponse } from '@_types/_api-response';
import { DropdownOption } from '@_types/atoms';
import { currentViewport, DEVICES } from '@helpers/breakpoint';

const getClass = getClassFunction('kf-o-country-selector-block');

const globalValue = 'global';

class CountrySelectorPrompt {
  element: HTMLDivElement;
  dropdown: HTMLDivElement | null;
  form: HTMLFormElement | null;
  cycleArrowWrapper: HTMLDivElement | null;
  cycleArrow: HTMLElement | null;
  countries: SiteCountryModelApiResponse[] = [];
  isCollapsed: boolean = true;

  constructor(element: HTMLDivElement) {
    this.element = element;
    this.dropdown = this.element.querySelector<HTMLDivElement>('.kf-a-dropdown');
    this.form = this.element.querySelector<HTMLFormElement>('.kf-o-country-selector-block__form');
    this.cycleArrowWrapper = this.element.querySelector<HTMLDivElement>('.kf-o-country-selector-block__cycle-arrow-wrapper');
    this.cycleArrow = this.element.querySelector<HTMLElement>('.kf-o-country-selector-block__cycle-arrow');
  }

  init() {
    this.addEventToDropdown();
    this.onDropdownInit();

    if (this.cycleArrow || this.cycleArrowWrapper) {
      this.initFormCollapseState();
      this.addEventToCycleArrow();
      this.addResizeListener();
    }
  }

  async handleGetSiteCountries() {
    const useCareersLink = this.element.dataset.useCareersLink || 'false';
    const useResearchLink = this.element.dataset.useResearchLink || 'false';

    const response = await getSiteCountries({
      data: {
        useCareersLink: useCareersLink,
        useResearchLink: useResearchLink,
      },
    });

    if (!response?.success) return;

    return response.data;
  }

  addEventToDropdown() {
    this.dropdown?.addEventListener('dropdown-change', this.onDropdownChange.bind(this));
  }

  triggerHeaderChangeHeightEvent() {
    if (currentViewport() === DEVICES.DESKTOP) return;

    const header = this.element.closest<HTMLElement>('.kf-o-header');

    header?.dispatchEvent(new CustomEvent('header-change-height-event'));
  }

  async onDropdownInit() {
    if (this.countries.length) return;

    const data = await this.handleGetSiteCountries();

    if (data?.countries?.length) {
      this.countries = data.countries;
      const globalLabel = this.element.dataset.globalLabel;
      const countryLabel = this.element.dataset.countryLabel;

      // Extract country code from placeholder if it exists
      const placeholderFlag = this.element.querySelector('.fflag');
      const placeholderGlobalIcon = this.element.querySelector('.kf-o-country-selector-block__global-icon');
      let selectedCountryCode = data.userCountryCode;
      let hasPreselectedCountry = false;

      if (placeholderFlag) {
        const flagClass = Array.from(placeholderFlag.classList).find((cls) => cls.startsWith('fflag-'));

        if (flagClass) {
          selectedCountryCode = flagClass.replace('fflag-', '').toLowerCase();
          hasPreselectedCountry = true;
        }
      } else if (placeholderGlobalIcon) {
        selectedCountryCode = globalValue;
        hasPreselectedCountry = true;
      }

      const userCountryExist = data.countries.find((country) => country.countryCode === selectedCountryCode);
      const global: SiteCountryModelApiResponse[] = [];
      const otherCountries: SiteCountryModelApiResponse[] = [];

      data.countries.forEach((country) => {
        if (country?.countryCode === globalValue) {
          global.push(country);

          return;
        }

        otherCountries.push(country);
      });

      const globalOptions = this.transformDataToDropdownOptions({
        countries: global,
        selected: hasPreselectedCountry && !userCountryExist,
        userCountryCode: selectedCountryCode,
      });
      const otherCountryOptions = this.transformDataToDropdownOptions({
        countries: otherCountries,
        userCountryCode: hasPreselectedCountry ? selectedCountryCode : undefined,
      });

      const dropdownOptions = this.dropdown?.querySelector<HTMLDivElement>('.kf-a-dropdown__options');

      if (!dropdownOptions) return;

      dropdownOptions.appendChild(this.renderGroupOption(globalOptions, globalLabel));
      dropdownOptions.appendChild(this.renderGroupOption(otherCountryOptions, countryLabel));

      this.dropdown?.dispatchEvent(new CustomEvent('init-options'));
    }
  }

  onDropdownChange(event: CustomEvent) {
    const value = event.detail.value as string;
    const text = event.detail.text as string;
    const link = event.detail.dataset.link;
    const labelText = this.dropdown?.querySelector<HTMLDivElement>('.kf-a-dropdown__label-text');

    if (!value || !text || !labelText) return;

    // Enable submit button when a country is selected
    this.enableSubmitButton();

    if (link) {
      const form = this.element.querySelector<HTMLFormElement>('.kf-o-country-selector-block__form');

      if (!form) return;

      form.action = link;

      // Update button href
      const buttonLink = this.element.querySelector<HTMLAnchorElement>('.kf-o-country-selector-block__form-button');

      if (buttonLink) {
        buttonLink.href = link;
      }
    }

    if (value !== globalValue) {
      labelText.replaceChildren(this.renderCountryLabelText(value, text));
    } else {
      labelText.replaceChildren(this.renderGlobalLabelText(text));
    }
  }

  enableSubmitButton() {
    const submitButton = this.element.querySelector<HTMLButtonElement>('.kf-o-country-selector-block__form-button');

    if (submitButton) {
      submitButton.classList.remove('kf-a-button--disabled');
    }
  }

  renderCountryLabelText(value: string, text: string): DocumentFragment {
    const fragment = document.createDocumentFragment();

    const flagSpan = document.createElement('span');

    flagSpan.className = `fflag fflag-${value.toUpperCase()} kf-o-country-selector-block__current-flag-icon`;
    flagSpan.title = text.trim();

    const textSpan = document.createElement('span');

    textSpan.className = 'kf-o-country-selector-block__current-country';
    textSpan.textContent = text.trim();

    fragment.appendChild(flagSpan);
    fragment.appendChild(textSpan);

    return fragment;
  }

  renderGlobalLabelText(text: string): DocumentFragment {
    const fragment = document.createDocumentFragment();

    const iconSpan = document.createElement('span');

    iconSpan.className = 'kf-o-country-selector-block__global-icon';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('class', 'kf-a-icon');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');

    use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/assets/images/icons.svg#kf-globe');
    svg.appendChild(use);
    iconSpan.appendChild(svg);

    const textSpan = document.createElement('span');

    textSpan.className = 'kf-o-country-selector-block__current-country';
    textSpan.textContent = text.trim();

    fragment.appendChild(iconSpan);
    fragment.appendChild(textSpan);

    return fragment;
  }

  renderGroupOption(options: DropdownOption[], label?: string): DocumentFragment {
    const fragment = document.createDocumentFragment();

    const groupDiv = document.createElement('div');

    groupDiv.className = 'kf-a-dropdown-group__item-group';
    groupDiv.setAttribute('role', 'group');
    groupDiv.setAttribute('aria-label', String(label || ''));

    const labelP = document.createElement('p');

    labelP.className = 'kf-a-dropdown-group__item-group-label';
    labelP.textContent = String(label || '');

    const optionsFragment = this.renderOptions(options);

    groupDiv.appendChild(labelP);
    groupDiv.appendChild(optionsFragment);
    fragment.appendChild(groupDiv);

    return fragment;
  }

  renderOptions(options: DropdownOption[]): DocumentFragment {
    const dropdownName = this.dropdown?.dataset?.name;
    const fragment = document.createDocumentFragment();

    options.forEach((option) => {
      fragment.appendChild(this.createOptionElement(option, dropdownName));
    });

    return fragment;
  }

  private createOptionElement(option: DropdownOption, dropdownName?: string): HTMLDivElement {
    const optionDiv = document.createElement('div');

    optionDiv.className = 'kf-a-dropdown__option kf-a-dropdown-group__child-option';
    optionDiv.setAttribute('role', 'option');
    optionDiv.setAttribute('aria-selected', String(option.selected));
    optionDiv.setAttribute('aria-label', String(option.text || ''));
    optionDiv.setAttribute('tabindex', '0');

    const input = this.createOptionInput(option, dropdownName);
    const label = this.createOptionLabel(option, dropdownName);

    optionDiv.appendChild(input);
    optionDiv.appendChild(label);

    return optionDiv;
  }

  private createOptionInput(option: DropdownOption, dropdownName?: string): HTMLInputElement {
    const input = document.createElement('input');

    input.id = `${dropdownName}${option?.value}`;
    input.name = String(dropdownName);
    input.value = option?.value || '';
    input.type = 'radio';
    if (option.selected) {
      input.checked = true;
    }
    if (option?.others?.link) {
      input.dataset.link = String(option.others.link);
    }

    return input;
  }

  private createOptionLabel(option: DropdownOption, dropdownName?: string): HTMLLabelElement {
    const label = document.createElement('label');

    label.htmlFor = `${dropdownName}${option?.value}`;

    const checkmark = document.createElement('span');

    checkmark.className = 'checkmark';

    const iconDiv = this.createOptionIcon(option);

    const textSpan = document.createElement('span');

    textSpan.className = `kf-o-country-selector-block__option-text${option.value !== globalValue ? ' kf-o-country-selector-block__country-option-text' : ''}`;
    textSpan.textContent = String(option?.text || '');

    label.appendChild(checkmark);
    label.appendChild(iconDiv);
    label.appendChild(textSpan);

    return label;
  }

  private createOptionIcon(option: DropdownOption): HTMLDivElement {
    const iconDiv = document.createElement('div');

    if (option.value !== globalValue) {
      iconDiv.className = `fflag fflag-${option.value?.toUpperCase()} kf-o-country-selector-block__flag-icon`;
      iconDiv.title = String(option.text || '');
    } else {
      iconDiv.className = 'kf-o-country-selector-block__global-icon';
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

      svg.setAttribute('class', 'kf-a-icon');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');

      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/assets/images/icons.svg#kf-globe');
      svg.appendChild(use);
      iconDiv.appendChild(svg);
    }

    return iconDiv;
  }

  // Mobile collapse/expand logic
  initFormCollapseState() {
    if (!this.form || !this.cycleArrow) return;

    if (this.isMobileView()) {
      this.isCollapsed = true;
      this.form.dataset.collapsed = 'true';
      this.form.style.maxHeight = '0';
      this.cycleArrow.classList.remove('kf-o-country-selector-block__cycle-arrow--expanded');
    } else {
      this.resetCollapseState();
    }
  }

  addEventToCycleArrow() {
    if (!this.cycleArrow || !this.cycleArrowWrapper) return;

    this.cycleArrow.addEventListener('click', this.toggleFormCollapse.bind(this));
    this.cycleArrow.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggleFormCollapse();
      }
    });

    this.cycleArrowWrapper.setAttribute('tabindex', '0');
    this.cycleArrowWrapper.setAttribute('role', 'button');
    this.cycleArrowWrapper.setAttribute('aria-label', 'Toggle country selector form');
  }

  addResizeListener() {
    window.addEventListener('resize', () => {
      if (this.isMobileView()) {
        if (this.form && !this.form.dataset.collapsed) {
          this.initFormCollapseState();
        } else if (this.form && this.form.dataset.collapsed === 'false') {
          this.syncExpandedState();
        }
      } else {
        this.resetCollapseState();
      }
    });
  }

  syncExpandedState() {
    if (!this.form || !this.cycleArrow) return;

    this.isCollapsed = false;
    this.form.dataset.animationDone = 'true';
    this.cycleArrow.classList.add('kf-o-country-selector-block__cycle-arrow--expanded');
    this.cycleArrowWrapper?.setAttribute('data-arrow-direction', 'up');
  }

  toggleFormCollapse() {
    if (!this.form || !this.cycleArrow || !this.isMobileView()) return;

    this.isCollapsed = !this.isCollapsed;

    if (this.isCollapsed) {
      this.collapseForm();
    } else {
      this.expandForm();
    }

    this.triggerHeaderChangeHeightEvent();
  }

  collapseForm() {
    if (!this.form || !this.cycleArrow) return;

    this.form.dataset.animationDone = 'false';

    this.form.style.maxHeight = `${this.form.scrollHeight}px`;

    void this.form.offsetHeight;

    this.form.dataset.collapsed = 'true';
    this.form.style.maxHeight = '0';
    this.cycleArrow.classList.remove('kf-o-country-selector-block__cycle-arrow--expanded');
    this.cycleArrowWrapper?.setAttribute('data-arrow-direction', 'down');
  }

  expandForm() {
    if (!this.form || !this.cycleArrow) return;

    this.form.dataset.collapsed = 'false';
    this.form.dataset.animationDone = 'false';
    this.form.style.maxHeight = `${this.form.scrollHeight}px`;
    this.cycleArrow.classList.add('kf-o-country-selector-block__cycle-arrow--expanded');
    this.cycleArrowWrapper?.setAttribute('data-arrow-direction', 'up');

    this.form.addEventListener(
      'transitionend',
      () => {
        if (!this.isCollapsed && this.form) {
          this.form.style.maxHeight = '';
          this.form.dataset.animationDone = 'true';
        }
      },
      { once: true }
    );
  }

  resetCollapseState() {
    if (!this.form || !this.cycleArrow) return;

    this.isCollapsed = false;
    this.form.dataset.collapsed = 'false';
    this.form.style.maxHeight = '';
    this.cycleArrow.classList.remove('kf-o-country-selector-block__cycle-arrow--expanded');
  }

  isMobileView(): boolean {
    return currentViewport() === DEVICES.MOBILE;
  }

  private transformDataToDropdownOptions({
    countries,
    selected,
    userCountryCode,
  }: {
    countries: SiteCountryModelApiResponse[];
    selected?: boolean;
    userCountryCode?: string;
  }): DropdownOption[] {
    return countries.map((country) => ({
      text: country.countryName,
      value: country.countryCode,
      selected: selected || userCountryCode === country.countryCode,
      others: {
        link: country.targetLink,
      },
    }));
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const countrySelectorPrompts = document.querySelectorAll<HTMLDivElement>(getClass());

  countrySelectorPrompts.forEach((element) => {
    new CountrySelectorPrompt(element).init();
  });
});

export {};
